import { BULK_LIST_VIEWS, bulkProgress, type BulkChange, type BulkList, type SortId, type ViewId } from "@omnabase/shared";
import { api, ApiError } from "./api";
import { dismiss, type toast as Toast } from "../shell/toast";
import type { ListSelection } from "./useListSelection";

/** The inbox's bulk change (moved out of the inbox screen in the select-whole-list round, 2026-10-29): the ticked numbers, or the whole list
 *  as the address narrows it with the count the bar showed; one toast with one Undo; a whole-list call says "Tagging 223 tickets…" while it
 *  runs and the result replaces it; a list that changed meanwhile (409 "changed") takes the whole list again and says its new number. */
export type ListAddress = { view: ViewId; project: string | null; q: string; tag: string | null; unread: boolean; urgent: boolean; sort: SortId | null };
type Result = { undoId: string; changed: number; summary: string; blocked?: number };

/** whether the view's whole list can be changed at once (never Home's 12-row shelves) */
export const wholeListable = (view: ViewId) => (BULK_LIST_VIEWS as string[]).includes(view);

/** the request's `list`: only what narrows it */
export function bulkListOf(a: ListAddress): BulkList {
  const q = a.q.trim();
  return { view: a.view, ...(a.project ? { project: a.project } : {}), ...(a.tag ? { tag: a.tag } : {}), ...(q ? { q } : {}), ...(a.unread ? { unread: true } : {}), ...(a.urgent ? { urgent: true } : {}), ...(a.sort ? { sort: a.sort } : {}) };
}

type Deps = { slug: string; selection: ListSelection; numbers: number[]; total: number; address: ListAddress; toast: typeof Toast; reload: () => void };

export async function runBulk(d: Deps, change: BulkChange): Promise<void> {
  const whole = d.selection.whole;
  const body = whole ? { list: bulkListOf(d.address), confirm: d.total, change } : { numbers: d.numbers, change };
  d.selection.clear();
  const wait = whole ? d.toast(bulkProgress(change, d.total), { kind: "info" }, 120_000) : null;
  try {
    const r = await api.post<Result>(`/api/v1/w/${d.slug}/tickets/bulk`, body);
    if (r.blocked) r.summary = `${r.summary} · ${r.blocked === 1 ? "1 sender" : `${r.blocked} senders`} blocked`;
    d.reload();
    d.toast(r.summary, { kind: "success", action: r.changed && change.kind !== "restore" ? { label: "Undo", onClick: () => void undo(d, r.undoId) } : undefined });
  } catch (e) {
    const changed = e instanceof ApiError && e.status === 409 ? (e.details as { reason?: string; count?: number } | undefined) : undefined;
    if (changed?.reason === "changed") { d.selection.selectWhole(); d.reload(); d.toast(`The list changed: it holds ${changed.count ?? 0} now`, { kind: "warning" }); return; }
    d.toast(e instanceof ApiError ? e.message : "Could not change the tickets", { kind: "error" });
  } finally { if (wait) dismiss(wait.id); }
}

async function undo(d: Deps, undoId: string): Promise<void> {
  try {
    const u = await api.post<{ restored: number; left: { number: number }[] }>(`/api/v1/w/${d.slug}/tickets/bulk/undo`, { undoId });
    d.reload();
    if (u.left.length) d.toast(`${u.restored} put back · ${u.left.length} changed since`, { kind: "success" });
  } catch (e) { d.toast(e instanceof ApiError ? e.message : "Could not take it back", { kind: "error" }); }
}
